import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { useToast } from '../context/ToastContext';
import { apiClient } from '../api/client';

const schema = z.object({
  email: z.string().email('Invalid email address'),
});

const ForgotPassword = () => {
  const { userType } = useParams();
  const [success, setSuccess] = useState(false);
  const { addToast } = useToast();
  
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema)
  });

  const onSubmit = async (data) => {
    try {
      await apiClient.post('/password-reset/request', {
        email: data.email,
        user_type: userType || 'student'
      });
      setSuccess(true);
      addToast('Reset link sent to your email', 'success');
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to request password reset', 'error');
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <Link to="/" className="auth-logo justify-center">
          <div className="auth-logo-icon">M</div>
          <div>Million<span>Minds</span></div>
        </Link>
        
        {!success ? (
          <>
            <div className="text-center mb-8">
              <h1 className="auth-title">Forgot Password?</h1>
              <p className="auth-subtitle">Enter your email and we'll send you a link to reset your password.</p>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)}>
              <Input
                label="Email Address"
                type="email"
                placeholder={`you@${userType === 'company' ? 'company.com' : 'college.edu'}`}
                {...register('email')}
                error={errors.email?.message}
              />
              
              <div className="mt-8">
                <Button type="submit" variant="primary" className="w-full" size="lg" isLoading={isSubmitting}>
                  Send Reset Link
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-success-light text-success rounded-full flex items-center justify-center text-3xl mx-auto mb-6">
              ✓
            </div>
            <h1 className="auth-title">Check your email</h1>
            <p className="auth-subtitle mb-8">
              We've sent a password reset link to your email address.
            </p>
            <Button variant="outline" className="w-full" onClick={() => setSuccess(false)}>
              Try another email
            </Button>
          </div>
        )}
        
        <div className="auth-footer mt-6">
          Remember your password?{' '}
          <Link to={userType === 'company' ? '/company/login' : '/login'}>
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
